import { Play, Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import { cn } from '@/lib/utils';
import { useCompanyToday } from '@/lib/session';

import { useBoard, useUpsertCell, useWeek } from './api';
import { useProjectPicker } from './project-picker';

/**
 * PRJ-03 §5f — the Timer: "a single centred card, headed Timer", a large
 * fixed-width HH:MM:SS clock, a project and a task, and ONE button — Start
 * when stopped, Stop & log when running. The first build shipped the weekly
 * grid without it; this is the tab's card, living on the same page as the
 * grid, because the screen has no intra-page tabs — which is also what keeps
 * ERR-PRJ-03-06's defect impossible (see the approvals-queue note on the
 * page).
 *
 * What stopping does (§5f.3): logs a DRAFT entry through the grid's own cell
 * write — the same PUT the weekly grid uses, no new state, no new endpoint —
 * noted "Timer", with the billable flag VISIBLE, pre-filled from the project
 * and editable before anything is saved (BR-PRJ-03-03; the flag rides the
 * card so the timer is not an entry point that stores a value the user could
 * not see). If the combination already holds hours today, the write sets the
 * combination's total (§15's duplicate rule: the grid shows the total), so a
 * run ADDS to the cell rather than overwriting it.
 *
 * Elapsed time is Date.now() arithmetic against the instant Start was
 * pressed — the interval below only repaints the clock; stopping never reads
 * a tick counter, so a throttled background tab cannot shrink the log.
 */

function sundayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return date.toISOString().slice(0, 10);
}

function clockFace(totalSeconds: number): string {
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = totalSeconds % 60;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

export function TimerCard({ act }: { act: (promise: Promise<unknown>) => void }) {
  const { t } = useTranslation();
  const today = useCompanyToday();
  const upsert = useUpsertCell();

  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [billable, setBillable] = useState(true);
  const [billableTouched, setBillableTouched] = useState(false);
  const [pickTask, setPickTask] = useState(false);
  const [startedAt, setStartedAt] = useState<number>();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const projectPicker = useProjectPicker(projectId === '' ? null : projectId);
  const project = projectPicker.byId.get(projectId);
  const { data: board } = useBoard(projectId === '' ? undefined : projectId);
  // The timer logs to TODAY, which need not be the week the grid is showing —
  // so it reads today's own week (same cache entry when they coincide).
  const { data: week } = useWeek(sundayOf(today));

  // BR-PRJ-03-03: pre-fill from the project — until the user chooses.
  useEffect(() => {
    if (!billableTouched && project) setBillable(project.billable);
  }, [project, billableTouched]);

  const running = startedAt !== undefined;

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  function start() {
    // ERR-PRJ-03-04: "Started the timer with no task | A message reads
    // 'Pick a task first'" — the refusal, with the §14 row's exact sentence.
    // The task dropdown below is labelled as REQUIRED (the prototype's
    // "— task (optional) —" wording is the defects-list entry this closes).
    if (projectId === '' || taskId === '') {
      setPickTask(true);
      return;
    }
    setPickTask(false);
    const now = Date.now();
    setNowMs(now);
    setStartedAt(now);
  }

  function stop() {
    if (startedAt === undefined) return;
    const elapsedHours = (Date.now() - startedAt) / 3_600_000;
    // BR-PRJ-03-13: a stopped timer logs AT LEAST 0.1 hours — §5f.2 "rounds
    // to two decimals with a floor of 0.1": a two-minute call is 0.10, a
    // 47-minute one 0.78. The floor also keeps the write off '0', which
    // would DELETE the cell (BR-PRJ-03-02) instead of logging.
    const logged = Math.max(0.1, Math.round(elapsedHours * 100) / 100);
    const merged = (week?.entries ?? []).filter(
      (entry) =>
        !entry.adjusting &&
        entry.projectId === projectId &&
        entry.taskId === taskId &&
        entry.workDate === today,
    );
    const existing = merged.reduce((sum, entry) => sum + Number.parseFloat(entry.hours), 0);
    const hours = (existing + logged).toFixed(2);
    act(
      upsert.mutateAsync({
        projectId,
        taskId,
        workDate: today,
        hours,
        billable,
        // §5f.3: the timer's own entry is noted "Timer". When the write merges
        // into hours somebody typed, their note is not overwritten.
        ...(merged.length === 0 ? { notes: 'Timer' } : {}),
      }),
    );
    setStartedAt(undefined);
  }

  const elapsedSeconds = running ? Math.max(0, Math.floor((nowMs - startedAt) / 1000)) : 0;

  return (
    <section aria-label={t('prj.timerTitle')}>
      <Card>
        <CardContent className="p-4">
          <div className="mx-auto max-w-lg space-y-3 text-center">
            <h2 className="text-sm font-semibold">{t('prj.timerTitle')}</h2>
            <p className="text-xs text-muted-foreground">{t('prj.timerHint')}</p>
            {/* The prototype bound this colour to a boolean and rendered an
                invalid value; the defects list says bind a COLOUR, so the
                running clock is green and the idle one muted. */}
            <div
              className={cn(
                'font-mono text-4xl font-semibold tabular-nums',
                running ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
              )}
            >
              <Ltr value={clockFace(elapsedSeconds)} />
            </div>
            {pickTask ? (
              <p
                role="alert"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
              >
                {t('prj.timerPickTask')}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {/* The Timer shares the timesheets page with the add-entry form,
                  and that form has a project picker and a task picker of its
                  own. `label` IS the accessible name — Typeahead forwards it
                  straight to aria-label on the combobox input — so naming both
                  "Project" put two nodes on the page under one name, and
                  getByLabel('Project', { exact: true }) resolved to 2 elements.
                  Same fix the billable checkbox below already carries: the
                  accessible name is distinct, the VISIBLE text is not. The
                  placeholder is what a reader sees, and it stays prj.fProject. */}
              <Typeahead
                label={t('prj.timerProject')}
                placeholder={t('prj.fProject')}
                value={projectId === '' ? null : projectId}
                onChange={(id) => {
                  setProjectId(id ?? '');
                  setTaskId('');
                  setBillableTouched(false);
                }}
                options={projectPicker.options}
                selected={projectPicker.optionFor(projectId)}
                query={projectPicker.query}
                onQueryChange={projectPicker.setQuery}
                emptyLabel={t('picker.noMatchingProject')}
                loading={projectPicker.loading}
                disabled={running}
                className="w-52"
              />
              {/* The same collision, the same fix: the add-entry form's task
                  <select> is also named by prj.colTask. The distinct name is the
                  aria-label only — the empty option a reader actually reads
                  stays prj.colTask, so the control still says "Task". */}
              <NativeSelect
                aria-label={t('prj.timerTask')}
                value={taskId}
                disabled={running}
                onChange={(event) => {
                  setTaskId(event.target.value);
                  if (event.target.value !== '') setPickTask(false);
                }}
                className="w-52"
              >
                <option value="">{t('prj.colTask')}</option>
                {(board?.tasks ?? []).map((task) => (
                  <option key={task.taskId} value={task.taskId}>
                    {task.title}
                  </option>
                ))}
              </NativeSelect>
              {/* Visible and editable before anything is saved (BR-PRJ-03-03).
                  A <span>, not a <label>: the accessible name is the distinct
                  aria-label, so this checkbox never answers a query for the
                  add-row form's identically worded one. */}
              <span className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  aria-label={t('prj.timerBillable')}
                  checked={billable}
                  disabled={running}
                  onChange={(event) => {
                    setBillable(event.target.checked);
                    setBillableTouched(true);
                  }}
                />
                {t('prj.fBillable')}
              </span>
              {running ? (
                <Button variant="destructive" onClick={stop}>
                  <Square className="h-4 w-4" /> {t('prj.timerStop')}
                </Button>
              ) : (
                <Button className="bg-emerald-700 text-white hover:bg-emerald-800" onClick={start}>
                  <Play className="h-4 w-4" /> {t('prj.timerStart')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
